import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  useConfirmarConciliacao,
  useFilaConciliacao,
} from "../../lib/conciliacao/hooks";
import {
  LABELS_MOTIVO,
  TX_TIPO_MAP,
  type Candidato,
  type ItemFilaConciliacao,
} from "../../lib/conciliacao/tipos";
import { formatarDataISO } from "../../lib/formato/data";
import { formatarDecimal } from "../../lib/formato/decimal";
import { ErroHttp } from "../../lib/http/erros";
import { exibirCnpjMascarado } from "./cnpj";
import { ConfirmacaoConciliacao } from "./ConfirmacaoConciliacao";
import { formatarScore } from "./score";

/*
 * Fila de conciliação manual (FE-3 CP-C — ESCRITA, a 1ª mutação que MOVE DINHEIRO).
 *
 * Esta fatia FIA a confirmação real sobre a leitura do CP-B. Cada item mostra a
 * TRANSAÇÃO bancária, o MOTIVO traduzido (`LABELS_MOTIVO` — a chave técnica NUNCA
 * aparece ao operador) e os CANDIDATOS sugeridos.
 *
 * CONFIRMAÇÃO EXPLÍCITA (UX dura): clicar "Confirmar" num candidato NÃO dispara o
 * POST — abre o passo `ConfirmacaoConciliacao` com o resumo do casamento. Só o
 * "Confirmar" DESSE passo dispara a mutação. Nunca há escrita por clique único.
 *
 * Erro de NEGÓCIO (via `erro.status`, pois a mensagem genérica do `ErroHttp` não
 * distingue 409 de 422):
 *  - 409 → "já resolvido" + refetch da fila (outro operador resolveu antes);
 *  - 422 → "candidato não é mais válido";
 *  - demais → a mensagem genérica do `ErroHttp`.
 *
 * `sem_candidato` (lista vazia) segue do CP-B: confirmar DESABILITADO + "Requer
 * tratamento manual".
 */

const SEM_VALOR = "—";

/** Consulta um mapa de rotulos `as const`, com fallback ao valor cru. */
function rotular(mapa: Record<string, string>, chave: string): string {
  return mapa[chave] ?? chave;
}

/** Par selecionado para o passo de confirmação (transação ⟷ candidato). */
interface Selecao {
  item: ItemFilaConciliacao;
  candidato: Candidato;
}

interface FilaConciliacaoProps {
  empresaId: string;
}

export function FilaConciliacao({
  empresaId,
}: FilaConciliacaoProps): JSX.Element {
  const consulta = useFilaConciliacao(empresaId, "aberta");
  const confirmacao = useConfirmarConciliacao(empresaId);
  const queryClient = useQueryClient();

  // Par escolhido para o passo de confirmação. `null` = nenhum modal aberto.
  const [selecao, setSelecao] = useState<Selecao | null>(null);
  // Mensagem de erro de negócio (409/422/demais) a exibir ao operador.
  const [erroNegocio, setErroNegocio] = useState<string | null>(null);

  function abrirConfirmacao(item: ItemFilaConciliacao, candidato: Candidato) {
    setErroNegocio(null);
    setSelecao({ item, candidato });
  }

  function fecharConfirmacao() {
    setSelecao(null);
  }

  function confirmarSelecao() {
    if (selecao === null) return;
    const { item, candidato } = selecao;
    confirmacao.mutate(
      { filaId: item.id, candidatoId: candidato.id },
      {
        onSuccess: () => {
          // `useConfirmarConciliacao` já invalida a fila no onSuccess do hook —
          // o item resolvido some no próximo fetch. Aqui só fechamos o passo.
          setErroNegocio(null);
          setSelecao(null);
        },
        onError: (erro) => {
          setSelecao(null);
          tratarErroNegocio(erro);
        },
      },
    );
  }

  function tratarErroNegocio(erro: unknown) {
    if (erro instanceof ErroHttp && erro.status === 409) {
      // Outro operador resolveu o item antes — revalida a fila e avisa.
      setErroNegocio("Este item já foi resolvido — atualizando a lista.");
      void queryClient.invalidateQueries({
        queryKey: ["conciliacao", "fila", empresaId],
      });
      return;
    }
    if (erro instanceof ErroHttp && erro.status === 422) {
      setErroNegocio("O candidato selecionado não é mais válido.");
      return;
    }
    if (erro instanceof ErroHttp) {
      setErroNegocio(erro.message);
      return;
    }
    setErroNegocio("Não foi possível confirmar a conciliação.");
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando fila de conciliação…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar a fila de conciliação.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const itens = consulta.data;

  if (itens.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Nenhum item na fila de conciliação.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {erroNegocio !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroNegocio}
        </p>
      )}

      <ul className="flex flex-col gap-4">
        {itens.map((item) => (
          <ItemFila
            key={item.id}
            item={item}
            aoEscolherCandidato={abrirConfirmacao}
          />
        ))}
      </ul>

      {selecao !== null && (
        <ConfirmacaoConciliacao
          transacao={selecao.item.transacao}
          candidato={selecao.candidato}
          aoConfirmar={confirmarSelecao}
          aoCancelar={fecharConfirmacao}
          processando={confirmacao.isPending}
        />
      )}
    </div>
  );
}

interface ItemFilaProps {
  item: ItemFilaConciliacao;
  aoEscolherCandidato: (
    item: ItemFilaConciliacao,
    candidato: Candidato,
  ) => void;
}

/**
 * Renderiza um item da fila: a transação bancária (esquerda) + os candidatos
 * sugeridos (direita). O motivo é sempre o rótulo PT-BR de `LABELS_MOTIVO`.
 */
function ItemFila({ item, aoEscolherCandidato }: ItemFilaProps): JSX.Element {
  const semCandidato = item.candidatos.length === 0;

  return (
    <li className="flex flex-col gap-3 rounded-md border border-platinum bg-snow p-4 font-corpo text-base text-off-black md:flex-row md:gap-8">
      <div className="flex flex-col gap-1">
        <h3 className="font-subtitulo">Transação</h3>
        <dl className="flex flex-col gap-1">
          <div className="flex gap-2">
            <dt className="font-subtitulo">Valor</dt>
            <dd>{formatarDecimal(item.transacao.valor)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-subtitulo">Data</dt>
            <dd>{formatarDataISO(item.transacao.data)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-subtitulo">Tipo</dt>
            <dd>{rotular(TX_TIPO_MAP, item.transacao.tipo)}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-subtitulo">CNPJ</dt>
            <dd>{item.transacao.cnpj_masc ?? SEM_VALOR}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-subtitulo">Descrição</dt>
            <dd>{item.transacao.descricao ?? SEM_VALOR}</dd>
          </div>
        </dl>
        <p className="mt-1">
          <span className="font-subtitulo">Motivo: </span>
          {LABELS_MOTIVO[item.motivo]}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="font-subtitulo">Candidatos</h3>
        {semCandidato ? (
          <div className="flex flex-col gap-1">
            <p className="text-flame">Requer tratamento manual</p>
            <button
              type="button"
              disabled
              className="self-start rounded-md border border-platinum bg-platinum px-3 py-1 text-off-black opacity-60"
            >
              Confirmar
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {item.candidatos.map((candidato) => (
              <LinhaCandidato
                key={candidato.id}
                candidato={candidato}
                aoEscolher={() => aoEscolherCandidato(item, candidato)}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

interface LinhaCandidatoProps {
  candidato: Candidato;
  aoEscolher: () => void;
}

/**
 * Uma linha de candidato com seu botão Confirmar. O clique NÃO escreve: ABRE o
 * passo de confirmação explícito (`ConfirmacaoConciliacao`). A escrita real só
 * ocorre no Confirmar daquele passo.
 */
function LinhaCandidato({
  candidato,
  aoEscolher,
}: LinhaCandidatoProps): JSX.Element {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <span>
        <span className="font-subtitulo">Valor: </span>
        {formatarDecimal(candidato.valor)}
      </span>
      <span>
        <span className="font-subtitulo">Score: </span>
        {formatarScore(candidato.score)}
      </span>
      <span>
        <span className="font-subtitulo">Previsão: </span>
        {formatarDataISO(candidato.data_prevista)}
      </span>
      <span>
        <span className="font-subtitulo">CNPJ: </span>
        {exibirCnpjMascarado(candidato.cnpj_masc)}
      </span>
      <button
        type="button"
        onClick={aoEscolher}
        className="rounded-md border border-flame bg-flame px-3 py-1 text-snow"
      >
        Confirmar
      </button>
    </li>
  );
}
